class WelcomeScratchPopup extends HTMLElement {
  constructor() {
    super();
    this.storageDismissKey = 'yina_popup_dismissed_until';
    this.storageSubscribedKey = 'yina_popup_subscribed';
    this.returnMarkerParam = 'yina_popup_subscribed';
  }

  connectedCallback() {
    this.fixReturnTo();

    if (this.shouldSkip()) return;

    this.settings = this.readSettings();
    this.panel = this.querySelector('.welcome-popup__panel');
    this.scratchStage = this.querySelector('[data-stage="scratch"]');
    this.emailStage = this.querySelector('[data-stage="email"]');
    this.scratchCard = this.querySelector('[data-scratch]');
    this.canvas = this.querySelector('[data-scratch-canvas]');
    this.revealFallback = this.querySelector('[data-reveal-fallback]');
    this.formView = this.querySelector('[data-email-form-view]');
    this.successView = this.querySelector('[data-success-view]');

    this.bindCloseHandlers();
    this.bindRevealFallback();
    this.setupScratchCanvas();

    this.checkSubscribedRedirect();
    this.scheduleShow();
  }

  // Sobrescribe el campo oculto return_to (generado por Shopify a partir de request.path)
  // con la URL completa actual, incluyendo cualquier parametro (preview_theme_id, etc.) mas
  // una marca propia. Esto hace que el destino de retorno viaje dentro del propio envio del
  // formulario en vez de depender de la cabecera Referer, que un paso intermedio como el
  // hCaptcha de Shopify puede no conservar.
  fixReturnTo() {
    try {
      const returnToInput = this.querySelector('input[name="return_to"]');
      if (!returnToInput) return;
      const url = new URL(window.location.href);
      url.searchParams.set(this.returnMarkerParam, '1');
      returnToInput.value = url.pathname + url.search;
    } catch (e) {
      /* si algo falla, se queda el valor por defecto de Liquid (request.path) */
    }
  }

  showFormView() {
    if (this.formView) this.formView.hidden = false;
    if (this.successView) this.successView.hidden = true;
  }

  showSuccessView() {
    if (this.formView) this.formView.hidden = true;
    if (this.successView) this.successView.hidden = false;
  }

  shouldSkip() {
    if (window.Shopify && window.Shopify.designMode) return false;
    if (window.location.pathname.indexOf('/checkout') === 0) return true;
    try {
      if (localStorage.getItem(this.storageSubscribedKey) === 'true') return true;
      const until = parseInt(localStorage.getItem(this.storageDismissKey) || '0', 10);
      if (until && Date.now() < until) return true;
    } catch (e) {
      /* localStorage unavailable: fail open rather than break */
    }
    return false;
  }

  readSettings() {
    const el = this;
    const num = (attr, fallback) => {
      const v = el.dataset[attr];
      const n = parseFloat(v);
      return isNaN(n) ? fallback : n;
    };
    return {
      delaySeconds: num('delaySeconds', 10),
      scrollPercent: num('scrollPercent', 40),
      suppressDays: num('suppressDays', 14),
    };
  }

  scheduleShow() {
    let shown = false;
    const show = () => {
      if (shown) return;
      shown = true;
      window.removeEventListener('scroll', onScroll);
      clearTimeout(timer);
      this.open();
    };

    const timer = setTimeout(show, (this.settings.delaySeconds || 10) * 1000);

    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const percent = (window.scrollY / scrollable) * 100;
      if (percent >= (this.settings.scrollPercent || 40)) show();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  open() {
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-open'));
    this.previouslyFocused = document.activeElement;
    const closeBtn = this.querySelector('.welcome-popup__close');
    if (closeBtn) closeBtn.focus();
    document.addEventListener('keydown', this.onKeydown = (e) => {
      if (e.key === 'Escape') this.close();
    });
  }

  close() {
    this.classList.remove('is-open');
    this.hidden = true;
    document.removeEventListener('keydown', this.onKeydown);
    try {
      const until = Date.now() + (this.settings.suppressDays || 14) * 24 * 60 * 60 * 1000;
      localStorage.setItem(this.storageDismissKey, String(until));
    } catch (e) {
      /* ignore */
    }
    if (this.previouslyFocused && this.previouslyFocused.focus) {
      this.previouslyFocused.focus();
    }
  }

  bindCloseHandlers() {
    this.querySelectorAll('[data-popup-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });
  }

  bindRevealFallback() {
    if (!this.revealFallback) return;
    this.revealFallback.addEventListener('click', () => this.revealPrize());
  }

  checkSubscribedRedirect() {
    const errorMsg = this.querySelector('[data-form-error]');
    if (errorMsg) {
      this.goToEmailStage();
      this.showFormView();
      this.open();
      return;
    }

    const serverSuccessMarker = this.querySelector('[data-form-success]');
    const urlIndicatesSuccess =
      window.location.search.indexOf('customer_posted=true') !== -1 ||
      window.location.search.indexOf(this.returnMarkerParam + '=1') !== -1;

    if (serverSuccessMarker || urlIndicatesSuccess) {
      try {
        localStorage.setItem(this.storageSubscribedKey, 'true');
      } catch (e) {
        /* ignore */
      }
      this.goToEmailStage();
      this.showSuccessView();
      this.open();
    }
  }

  goToEmailStage() {
    if (this.scratchStage) this.scratchStage.hidden = true;
    if (this.emailStage) this.emailStage.hidden = false;
  }

  revealPrize() {
    if (this.scratchCard) this.scratchCard.classList.add('is-revealed');
    setTimeout(() => this.goToEmailStage(), 500);
  }

  setupScratchCanvas() {
    if (!this.canvas || !this.canvas.getContext) {
      this.revealPrize();
      return;
    }
    const ctx = this.canvas.getContext('2d');
    const width = this.canvas.width;
    const height = this.canvas.height;

    const paintOverlay = () => {
      ctx.globalCompositeOperation = 'source-over';
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#B29262');
      gradient.addColorStop(1, '#6B2737');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = 'rgba(247, 244, 239, 0.9)';
      ctx.font = '13px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Rasca aquí', width / 2, height / 2);
    };
    paintOverlay();

    let scratching = false;
    let revealed = false;

    const scratchAt = (x, y) => {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(x, y, 18, 0, Math.PI * 2);
      ctx.fill();
    };

    const getPos = (evt) => {
      const rect = this.canvas.getBoundingClientRect();
      const point = evt.touches ? evt.touches[0] : evt;
      return {
        x: ((point.clientX - rect.left) / rect.width) * width,
        y: ((point.clientY - rect.top) / rect.height) * height,
      };
    };

    const checkRevealPercent = () => {
      if (revealed) return;
      const imageData = ctx.getImageData(0, 0, width, height).data;
      let transparent = 0;
      for (let i = 3; i < imageData.length; i += 4 * 20) {
        if (imageData[i] === 0) transparent++;
      }
      const total = imageData.length / (4 * 20);
      if (transparent / total > 0.45) {
        revealed = true;
        this.revealPrize();
      }
    };

    const start = (evt) => {
      scratching = true;
      const { x, y } = getPos(evt);
      scratchAt(x, y);
    };
    const move = (evt) => {
      if (!scratching) return;
      evt.preventDefault();
      const { x, y } = getPos(evt);
      scratchAt(x, y);
      checkRevealPercent();
    };
    const end = () => {
      scratching = false;
      checkRevealPercent();
    };

    this.canvas.addEventListener('pointerdown', start);
    this.canvas.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    this.canvas.addEventListener('touchstart', start, { passive: true });
    this.canvas.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', end);
  }
}

customElements.define('welcome-scratch-popup', WelcomeScratchPopup);
